import { useMemo, useCallback, useState, useEffect } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getTagsByIds, tagManager } from "@/shared/api/newsquawk-headlines";
import { useFilterBuilder, type FilterDraft } from "../hooks/useFilterBuilder";
import type { SavedWatchlist } from "../listTypes";
import type { Portfolio } from "@/types/Portfolio";
import { isPlaceholderTag } from "../hooks/tagCategories";
import { formatListName } from "@/shared/utils/formatListName";
import { savedViewColor } from "@/shared/utils/savedViewHighlight";
import QueryBuilderPane from "./QueryBuilderPane";
import ListPicker, { type PickerRow } from "./builder/ListPicker";
import toast from "react-hot-toast";

// Static seed from menuCategories
const menuTagIdToName: Record<string, string> = {};
menuCategories.forEach((cat) => {
  cat.items.forEach((item) => {
    menuTagIdToName[String(item.id)] = item.title;
  });
});

interface FilterBuilderBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  queryId?: number | null;
  /**
   * A plain watchlist to edit. This is the one builder: typed terms, tags and
   * lists together, saved as whichever record they need — see listStorage.ts.
   */
  portfolioId?: number | null;
  /**
   * A tag watchlist to edit: saved as a combined watchlist in its place, or
   * in place when it is a Newsquawk one the desk is editing.
   */
  tagWatchlistId?: number | null;
  /** Concierge draft pre-fill (create mode) */
  draft?: FilterDraft | null;
  /** Guided demo only — see UseFilterBuilderOptions.portfoliosOverride. */
  portfoliosOverride?: Portfolio[];
  /**
   * What was saved, so the page behind can do what it does after any other
   * save — preview it, and point at it in your lists. Closing alone left the
   * one question a save asks ("did that work?") unanswered on this route,
   * which is the one the concierge hands off to.
   */
  onSaved?: (saved?: SavedWatchlist) => void;
}

const unique = <T,>(items: T[]): T[] => [...new Set(items)];

/**
 * The watchlist builder: every watchlist on the left, the rule being built on
 * the right.
 */
export default function FilterBuilderBrowser({
  isOpen,
  onClose,
  queryId,
  portfolioId,
  tagWatchlistId,
  draft,
  portfoliosOverride,
  onSaved,
}: FilterBuilderBrowserProps) {
  const builder = useFilterBuilder({
    queryId,
    portfolioId,
    tagWatchlistId,
    draft,
    portfoliosOverride,
    // A save with no id created nothing to point at — a colour kept for a
    // Newsquawk list, or the guided demo, which writes nothing at all.
    onSaved: (_name, categories, id, kind = "combined") => {
      onSaved?.(
        id === undefined
          ? undefined
          : kind === "tag"
            ? { kind, id, tags: categories }
            : { kind, id }
      );
      handleClose();
    },
    onDeleted: () => handleClose(),
  });

  /**
   * Every way out clears the form, not only Cancel.
   *
   * This stays mounted while closed, so a save or delete that only closed it
   * left that list waiting in the next new one — its name, its picks, and for
   * the desk a ticked "Publish to the Newsquawk library". An edit or a draft
   * re-seeds the form on the way in; a plain new one has nothing to seed it.
   */
  const handleClose = useCallback(() => {
    builder.reset();
    onClose();
  }, [builder.reset, onClose]);

  // ── Tag names ─────────────────────────────────────────────────────────
  const [resolvedTagNames, setResolvedTagNames] = useState<Record<string, string>>({});
  /** Tags of rows opened in the picker, looked up on demand. */
  const [wantedTagIds, setWantedTagIds] = useState<string[]>([]);

  const tagName = useCallback(
    (id: string) =>
      menuTagIdToName[id] ??
      resolvedTagNames[id] ??
      // A tag just picked out of the library search is already named in the
      // shared cache, so this costs no second lookup.
      tagManager.getCached(Number(id))?.name ??
      `Tag #${id}`,
    [resolvedTagNames]
  );

  useEffect(() => {
    /**
     * The selected tags belong here as much as the watchlists' do: a tag the
     * concierge picked from outside the 51 menu ones has no name to show
     * until it's looked up. Rows opened in the picker add theirs.
     */
    const allTagIds = [
      ...builder.tagsWatchlists.flatMap((tw) => tw.tags),
      ...builder.selectedCategories.map((c) => String(c.id)),
      ...wantedTagIds,
    ];
    const unknownIds = unique(
      allTagIds.filter((id) => !menuTagIdToName[id] && !resolvedTagNames[id])
    );
    if (unknownIds.length === 0) return;

    getTagsByIds(unknownIds.join(" "))
      .then((tagDetails) => {
        const newNames: Record<string, string> = {};
        const kinds: Record<string, string> = {};
        if (tagDetails) {
          for (const [tagId, detail] of Object.entries(tagDetails)) {
            const primary = detail.labels?.find((l) => l.primary);
            const name = primary?.name || detail.labels?.[0]?.name;
            if (name) newNames[tagId] = name;
            if (detail.category) kinds[tagId] = detail.category;
          }
        }
        // Ids the service doesn't know keep their placeholder, and aren't
        // asked about again.
        for (const id of unknownIds) newNames[id] ??= `Tag #${id}`;
        setResolvedTagNames((prev) => ({ ...prev, ...newNames }));
        builder.rememberTagKinds(kinds);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rememberTagKinds is stable
  }, [builder.tagsWatchlists, builder.selectedCategories, wantedTagIds]);

  // Swap the `Tag #123` placeholders the builder seeded for the real names, so
  // the chips read as tags rather than as ids.
  useEffect(() => {
    const needsRename = builder.selectedCategories.some(
      (c) =>
        isPlaceholderTag(c) &&
        resolvedTagNames[String(c.id)] &&
        resolvedTagNames[String(c.id)] !== c.title
    );
    if (!needsRename) return;
    builder.setSelectedCategories((prev) =>
      prev.map((c) => {
        const name = resolvedTagNames[String(c.id)];
        return name && isPlaceholderTag(c) ? { ...c, title: name } : c;
      })
    );
  }, [resolvedTagNames, builder.selectedCategories, builder.setSelectedCategories]);

  // ── The picker's rows ─────────────────────────────────────────────────
  const rows = useMemo<PickerRow[]>(() => {
    const out: PickerRow[] = [];
    for (const p of builder.portfolios) {
      // A list can't include itself.
      if (builder.editingKind === "watchlist" && p.id === portfolioId) continue;
      out.push({
        key: `p-${p.id}`,
        kind: "watchlist",
        id: p.id,
        name: formatListName(p.name),
        color: p.color,
        owner: p.owner_type === "NEWSQUAWK" ? "desk" : "mine",
        checked: builder.selectedPortfolioIds.includes(p.id),
        contents: { tickers: p.tickers, keywords: p.keywords ?? [], tags: [] },
      });
    }
    for (const tw of builder.tagsWatchlists) {
      if (builder.editingKind === "tag" && tw.id === tagWatchlistId) continue;
      out.push({
        key: `t-${tw.id}`,
        kind: "tag",
        id: tw.id,
        name: formatListName(tw.name),
        color: null,
        owner: tw.owner_type === "USER" ? "mine" : "desk",
        checked: builder.selectedTagsWatchlistIds.includes(tw.id),
        contents: { tickers: [], keywords: [], tags: tw.tags.map(tagName) },
      });
    }
    for (const q of builder.combinedLists) {
      if (q.id === undefined) continue;
      const portfolios = q.portfolios ?? [];
      out.push({
        key: `c-${q.id}`,
        kind: "combined",
        id: q.id,
        name: formatListName(q.queryName),
        color: savedViewColor(q),
        owner: q.owner_type === "NEWSQUAWK" ? "desk" : "mine",
        checked: builder.includedIds.includes(q.id),
        contents: {
          tickers: unique(portfolios.flatMap((p) => p.tickers)),
          keywords: unique(portfolios.flatMap((p) => p.keywords ?? [])),
          tags: unique([
            ...(q.categories ?? []).map(String),
            ...(q.tagsWatchlists ?? []).flatMap((tw) => tw.tags),
          ]).map(tagName),
          expression: q.searchKeyword?.trim() || undefined,
        },
      });
    }
    return out;
  }, [
    builder.portfolios,
    builder.tagsWatchlists,
    builder.combinedLists,
    builder.selectedPortfolioIds,
    builder.selectedTagsWatchlistIds,
    builder.includedIds,
    builder.editingKind,
    portfolioId,
    tagWatchlistId,
    tagName,
  ]);

  const handleToggle = useCallback(
    (row: PickerRow) => {
      /**
       * A list edited in place takes nothing that would turn it into a
       * combined one — see inPlaceReason in useFilterBuilder.
       */
      if (builder.inPlace) {
        toast(
          builder.inPlaceReason === "ingredient"
            ? "Another of your watchlists includes this one, so it stays a list of tags. To combine it, make a new watchlist that includes it."
            : "This Newsquawk list stays as it is. To combine it, make a new watchlist that includes it."
        );
        return;
      }
      if (row.kind === "watchlist") builder.togglePortfolio(row.id);
      else if (row.kind === "tag") builder.toggleTagWatchlist(row.id);
      else builder.toggleIncluded(row.id);
    },
    [builder.inPlace, builder.inPlaceReason, builder.togglePortfolio, builder.toggleTagWatchlist, builder.toggleIncluded]
  );

  const handleExpand = useCallback(
    (row: PickerRow) => {
      if (row.kind !== "combined") return;
      const q = builder.combinedLists.find((c) => c.id === row.id);
      const ids = [
        ...(q?.categories ?? []).map(String),
        ...(q?.tagsWatchlists ?? []).flatMap((tw) => tw.tags),
      ];
      if (ids.length > 0) setWantedTagIds((prev) => unique([...prev, ...ids]));
    },
    [builder.combinedLists]
  );

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const title = builder.isEditing
    ? "Edit watchlist"
    : builder.basedOn
      ? `Customise “${builder.basedOn}”`
      : "New watchlist";

  return (
    <Dialog open={isOpen} onClose={handleClose} className="no-drag relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-950/85 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 motion-reduce:transition-none"
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-2 md:p-4">
        <DialogPanel
          transition
          className="flex h-[90vh] w-full max-w-[1240px] transform flex-col overflow-hidden rounded-lg border border-gray-800 bg-gray-900 text-gray-300 shadow-xl shadow-black/50 transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 motion-reduce:transition-none data-[closed]:sm:scale-95"
        >
          {/* Mobile fallback */}
          {isMobile ? (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
              <ComputerDesktopIcon className="mb-4 size-12 text-gray-600" />
              <DialogTitle className="mb-2 text-sm font-semibold text-gray-200">
                Desktop Required
              </DialogTitle>
              <p className="mb-6 text-xs text-gray-500">
                Watchlists are built and edited on a larger screen.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="h-9 rounded-md border-0 bg-gray-700 px-4 py-0 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <header className="flex h-[52px] shrink-0 items-center gap-3 border-b border-gray-800 pl-5 pr-3">
                <DialogTitle className="m-0 flex-1 truncate text-[15px] font-semibold text-gray-100">
                  {title}
                </DialogTitle>
                <button
                  type="button"
                  onClick={handleClose}
                  aria-label="Close"
                  className="flex size-8 items-center justify-center rounded-md border-0 bg-transparent p-0 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
                >
                  <XMarkIcon className="size-5" aria-hidden="true" />
                </button>
              </header>
              <div className="flex min-h-0 flex-1">
                <ListPicker rows={rows} onToggle={handleToggle} onExpand={handleExpand} />
                <QueryBuilderPane builder={builder} onCancel={handleClose} />
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
